import { InfoIcon, UserPlus } from 'lucide-react'
import { FC, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Label } from 'recharts'
import { z } from 'zod'

import LoadingButton from '@/components/loading-button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Invitation } from '@/out/invitation'

import { zodResolver } from '@hookform/resolvers/zod'
import useSendInvitation from './useSendInvitation'

const schema = z
  .object({
    email: z.string().email(),
  })
  .required()

type FormData = z.infer<typeof schema>

type Props = {
  onCreate: (invitation: Invitation) => void
}

const AddAdmin: FC<Props> = ({ onCreate }) => {
  const [isOpen, setIsOpen] = useState(false)

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
    },
    mode: 'onSubmit',
  })

  const [sendInvitation, { timer }] = useSendInvitation(i => {
    onCreate(i)
    setIsOpen(false)
    form.reset()
  })

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-admin-button" variant="outline">
          <UserPlus className="w-4 h-4" />
          Add an Administrator
        </Button>
      </DialogTrigger>
      <DialogContent className="space-y-4">
        <form onSubmit={form.handleSubmit(sendInvitation)}>
          <DialogHeader>
            <DialogTitle>Add an Administrator</DialogTitle>
            {window.isZoom && (
              <DialogDescription>
                <Alert variant="information" className="pt-4 mt-4">
                  <InfoIcon className="w-4 h-4" />
                  <AlertDescription>
                    <strong>No invitation email will be sent</strong>. Simply have them close then re-open the Zoom app
                    (or install it from the Zoom marketplace), and they&apos;ll be given access to the admin dashboard.
                  </AlertDescription>
                </Alert>
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input
              {...form.register('email')}
              autoComplete="one-time-code"
              placeholder="Enter new administrator email"
              data-testid="admin-email-input"
            />
            {form.formState.errors.email && (
              <p className="text-sm text-red-500">{form.formState.errors.email.message}</p>
            )}
          </div>
          <DialogFooter>
            <LoadingButton data-testid="add-admin-button-confirm" timer={timer} type="submit">
              Add Administrator
            </LoadingButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default AddAdmin
